
import type {
  CompanyWithCounts,
  CreateCompanyBody,
  CreateUserBody,
  SeatWithUser,
} from "@api/app-api";
import { CompanyTierEnum } from "@global/types/company";
import AddIcon from "@mui/icons-material/Add";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../api";
import { config, type Environment } from "../../config";

// ============================================================================
// TYPES
// ============================================================================

interface ImportRow {
  id: number;
  companyName: string;
  email: string;
  firstName: string;
  lastName: string;
  seatMode: string;
  seatType: string;
}

interface CompanyPlan {
  name: string;
  existingId?: string; // set if company already exists
  users: {
    rowId: number;
    email: string;
    firstName: string;
    lastName: string;
    seatMode: string;
    seatType: string;
  }[];
}

interface CreationResult {
  type: "company" | "user" | "seat";
  name: string;
  success: boolean;
  message: string;
}

interface CompanySettings {
  tier: CompanyTierEnum;
  expiryDate: string;
  maxUserCount: string;
  adminRowId?: number;
}

type SeatMode = "none" | "create" | "assign";
type SeatType = "named" | "flexible";

interface UserSeatChoice {
  mode: SeatMode;
  seatType: SeatType;
  seatId?: string;
}

const DEFAULT_EXPIRY = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split("T")[0];
};

const DEFAULT_SETTINGS: CompanySettings = {
  tier: CompanyTierEnum.TRIAL,
  expiryDate: DEFAULT_EXPIRY(),
  maxUserCount: "",
};

const DEFAULT_SEAT_CHOICE: UserSeatChoice = {
  mode: "create",
  seatType: "named",
};

// ============================================================================
// HELPERS
// ============================================================================

let nextRowId = 1;

type ImportColumnKey =
  | "company"
  | "email"
  | "firstName"
  | "lastName"
  | "seat"
  | "seatType";

type ColumnMap = Record<ImportColumnKey, number>;

const HEADER_SPECS: Array<{
  key: ImportColumnKey;
  exact: string[];
  contains: string[];
}> = [
  {
    key: "company",
    exact: ["company", "companyname", "organisation", "organization", "org"],
    contains: ["company", "organisation", "organization", "customer", "client"],
  },
  {
    key: "email",
    exact: ["email", "emailaddress", "emailadress", "mail", "e-mail"],
    contains: ["email"],
  },
  {
    key: "firstName",
    exact: [
      "firstname",
      "first",
      "givenname",
      "given",
      "forename",
      "vorname",
      "fname",
    ],
    contains: ["firstname", "givenname", "forename", "vorname"],
  },
  {
    key: "lastName",
    exact: [
      "lastname",
      "last",
      "familyname",
      "family",
      "surname",
      "nachname",
      "lname",
    ],
    contains: ["lastname", "familyname", "surname", "nachname"],
  },
  {
    key: "seatType",
    exact: [
      "seattype",
      "seatkind",
      "licensetype",
      "licencetype",
      "licensekind",
    ],
    contains: [
      "seattype",
      "seatkind",
      "licensetype",
      "licencetype",
      "licensekind",
    ],
  },
  {
    key: "seat",
    exact: ["seat", "seataction", "seatmode", "license", "licence"],
    contains: ["seataction", "seatmode", "licenseaction", "licenceaction"],
  },
];

function emptyColumnMap(): ColumnMap {
  return {
    company: -1,
    email: -1,
    firstName: -1,
    lastName: -1,
    seat: -1,
    seatType: -1,
  };
}

function normalizeHeader(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "");
}

function levenshteinDistance(left: string, right: string) {
  let previousRow = Array.from(
    { length: right.length + 1 },
    (_, index) => index,
  );

  for (let leftIndex = 1; leftIndex <= left.length; leftIndex++) {
    const currentRow = [leftIndex];
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex++) {
      const substitutionCost =
        left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1;
      currentRow[rightIndex] = Math.min(
        previousRow[rightIndex] + 1,
        currentRow[rightIndex - 1] + 1,
        previousRow[rightIndex - 1] + substitutionCost,
      );
    }
    previousRow = currentRow;
  }

  return previousRow[right.length];
}

function getHeaderScore(
  normalizedHeader: string,
  spec: (typeof HEADER_SPECS)[number],
) {
  if (!normalizedHeader) return 0;

  const exactAliases = spec.exact.map(normalizeHeader);
  const containsAliases = spec.contains.map(normalizeHeader);

  if (exactAliases.includes(normalizedHeader)) return 100;
  if (containsAliases.some((alias) => normalizedHeader.includes(alias))) {
    return 90;
  }

  const fuzzyAliases = [...exactAliases, ...containsAliases].filter(
    (alias) => alias.length >= 5 && normalizedHeader.length >= 5,
  );
  const bestDistance = fuzzyAliases.reduce<number | null>((best, alias) => {
    const distance = levenshteinDistance(normalizedHeader, alias);
    return best === null || distance < best ? distance : best;
  }, null);

  if (bestDistance === null) return 0;
  const maxDistance = normalizedHeader.length >= 8 ? 2 : 1;
  return bestDistance <= maxDistance ? 80 - bestDistance : 0;
}

function detectColumns(headers: string[]): ColumnMap {
  const colMap = emptyColumnMap();
  const normalizedHeaders = headers.map(normalizeHeader);
  const usedIndexes = new Set<number>();

  for (const spec of HEADER_SPECS) {
    let bestIndex = -1;
    let bestScore = 0;

    normalizedHeaders.forEach((normalizedHeader, columnIndex) => {
      if (usedIndexes.has(columnIndex)) return;
      const score = getHeaderScore(normalizedHeader, spec);
      if (score > bestScore) {
        bestIndex = columnIndex;
        bestScore = score;
      }
    });

    if (bestScore >= 78) {
      colMap[spec.key] = bestIndex;
      usedIndexes.add(bestIndex);
    }
  }

  return colMap;
}

function parseClipboardData(text: string): ImportRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length === 0) return [];

  // Detect delimiter: tab or comma
  const firstLine = lines[0];
  const delimiter = firstLine.includes("\t") ? "\t" : ",";

  // Parse header row - try to match columns by name
  const headers = lines[0]
    .split(delimiter)
    .map((header) => header.trim().replace(/['"]/g, ""));

  const colMap = detectColumns(headers);

  // If no headers detected, assume order: company, email, first, last
  const hasHeaders = Object.values(colMap).some((v) => v >= 0);
  if (!hasHeaders) {
    const colCount = headers.length;
    if (colCount >= 4) {
      colMap.company = 0;
      colMap.email = 1;
      colMap.firstName = 2;
      colMap.lastName = 3;
      if (colCount >= 5) colMap.seat = 4;
      if (colCount >= 6) colMap.seatType = 5;
    } else if (colCount === 3) {
      colMap.email = 0;
      colMap.firstName = 1;
      colMap.lastName = 2;
    }
  }

  const dataLines = hasHeaders ? lines.slice(1) : lines;
  const rows: ImportRow[] = [];

  for (const line of dataLines) {
    if (!line.trim()) continue;
    const cols = line
      .split(delimiter)
      .map((c) => c.trim().replace(/^["']|["']$/g, ""));

    const row: ImportRow = {
      id: nextRowId++,
      companyName: colMap.company >= 0 ? cols[colMap.company] || "" : "",
      email: colMap.email >= 0 ? cols[colMap.email] || "" : "",
      firstName: colMap.firstName >= 0 ? cols[colMap.firstName] || "" : "",
      lastName: colMap.lastName >= 0 ? cols[colMap.lastName] || "" : "",
      seatMode: normalizeSeatModeInput(
        colMap.seat >= 0 ? cols[colMap.seat] : undefined,
      ),
      seatType: normalizeSeatTypeInput(
        colMap.seatType >= 0 ? cols[colMap.seatType] : undefined,
      ),
    };

    if (!row.companyName && !row.email && !row.firstName && !row.lastName)
      continue;
    rows.push(row);
  }

  return rows;
}

function buildCompanyPlan(
  rows: ImportRow[],
  existingCompanies: CompanyWithCounts[],
): CompanyPlan[] {
  const companyMap = new Map<string, CompanyPlan>();

  // Index existing companies by lowercase name for matching
  const existingByName = new Map<string, CompanyWithCounts>();
  for (const c of existingCompanies) {
    existingByName.set(c.name.trim().toLowerCase(), c);
  }

  for (const row of rows) {
    if (!row.companyName || !row.email) continue;
    const key = row.companyName.trim().toLowerCase();

    if (!companyMap.has(key)) {
      const existing = existingByName.get(key);
      companyMap.set(key, {
        name: existing?.name ?? row.companyName.trim(),
        existingId: existing?.id,
        users: [],
      });
    }

    companyMap.get(key)!.users.push({
      rowId: row.id,
      email: row.email.trim(),
      firstName: row.firstName.trim(),
      lastName: row.lastName.trim(),
      seatMode: row.seatMode,
      seatType: row.seatType,
    });
  }

  return Array.from(companyMap.values());
}

function getCompanyKey(companyName: string) {
  return companyName
    .trim()
    .toLowerCase()
    .replace(/[.,;:]/g, "")
    .replace(/\s+/g, " ");
}

function getSeatChoiceKey(companyName: string, rowId: number) {
  return `${getCompanyKey(companyName)}:${rowId}`;
}

function parseSeatMode(value: string): SeatMode | undefined {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return DEFAULT_SEAT_CHOICE.mode;
  if (["create", "assign", "none"].includes(normalized)) {
    return normalized as SeatMode;
  }
  return undefined;
}

function parseSeatType(value: string): SeatType | undefined {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return DEFAULT_SEAT_CHOICE.seatType;
  if (["named", "flexible"].includes(normalized)) {
    return normalized as SeatType;
  }
  return undefined;
}

function normalizeSeatModeInput(value: string | undefined) {
  const input = (value ?? "").trim().toLowerCase();
  return parseSeatMode(input) ?? input;
}

function normalizeSeatTypeInput(value: string | undefined) {
  const input = (value ?? "").trim().toLowerCase();
  return parseSeatType(input) ?? input;
}

function getRowSeatChoice(row: Pick<ImportRow, "seatMode" | "seatType">) {
  return {
    mode: parseSeatMode(row.seatMode) ?? DEFAULT_SEAT_CHOICE.mode,
    seatType: parseSeatType(row.seatType) ?? DEFAULT_SEAT_CHOICE.seatType,
  };
}

function getSeatType(seat: SeatWithUser): SeatType {
  return seat.isFlexible ? "flexible" : "named";
}

function formatSeatType(type: SeatType) {
  return type === "flexible" ? "Flexible" : "Named";
}

function formatSeatLabel(seat: SeatWithUser) {
  return `${seat.id.slice(0, 8)}... (${formatSeatType(getSeatType(seat))})`;
}

function parseMaxUserCount(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

// ============================================================================
// BULK IMPORT PAGE
// ============================================================================

interface BulkImportPageProps {
  environment: Environment;
}

export const BulkImportPage: React.FC<BulkImportPageProps> = ({
  environment,
}) => {
  const seatManagementEnabled = config.supportsSeatManagement(environment);
  const [pasteValue, setPasteValue] = useState("");
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState<CreationResult[]>([]);
  const [progressIndex, setProgressIndex] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [seatChoices, setSeatChoices] = useState<
    Record<string, UserSeatChoice>
  >({});
  const [companySeats, setCompanySeats] = useState<
    Record<string, SeatWithUser[]>
  >({});
  const [loadingSeats, setLoadingSeats] = useState(false);

  // Per-company settings for new companies (keyed by lowercase company name)
  const [companySettings, setCompanySettings] = useState<
    Record<string, CompanySettings>
  >({});

  // Existing companies for matching
  const [existingCompanies, setExistingCompanies] = useState<
    CompanyWithCounts[]
  >([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const companies = await apiClient.getCompanies();
      setExistingCompanies(companies);
    } catch {
      // Non-critical - we can still create new companies
      console.error("Failed to load existing companies");
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // ---- Paste handling ----
  const handleParse = () => {
    setError(null);
    const parsed = parseClipboardData(pasteValue);
    if (parsed.length === 0) {
      setError(
        "No data could be parsed. Make sure columns include Company, Email, First Name, Last Name.",
      );
      return;
    }
    setRows(parsed);
    setSeatChoices({});
    setCompanySettings({});
  };

  // ---- Row editing ----
  const updateRow = (
    id: number,
    field: Exclude<keyof ImportRow, "id">,
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const deleteRow = (id: number) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: nextRowId++,
        companyName: "",
        email: "",
        firstName: "",
        lastName: "",
        seatMode: DEFAULT_SEAT_CHOICE.mode,
        seatType: DEFAULT_SEAT_CHOICE.seatType,
      },
    ]);
  };

  // ---- Validation ----
  const duplicateRowKeys = rows.reduce<Record<string, number>>((acc, row) => {
    const key = `${getCompanyKey(row.companyName)}:${row.email.trim().toLowerCase()}`;
    if (row.companyName.trim() && row.email.trim()) {
      acc[key] = (acc[key] ?? 0) + 1;
    }
    return acc;
  }, {});

  const validationErrors = rows.reduce<Record<number, string[]>>((acc, row) => {
    const errs: string[] = [];
    if (!row.companyName.trim()) errs.push("Company name required");
    if (!row.email.trim()) errs.push("Email required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email.trim()))
      errs.push("Invalid email");
    else if (
      duplicateRowKeys[
        `${getCompanyKey(row.companyName)}:${row.email.trim().toLowerCase()}`
      ] > 1
    ) {
      errs.push("Duplicate email for this company");
    }
    if (seatManagementEnabled) {
      if (!parseSeatMode(row.seatMode)) {
        errs.push("Seat action must be create, assign, or none");
      }
      if (!parseSeatType(row.seatType)) {
        errs.push("Seat type must be named or flexible");
      }
    }
    if (errs.length > 0) acc[row.id] = errs;
    return acc;
  }, {});

  const hasErrors = Object.keys(validationErrors).length > 0;
  const plan = buildCompanyPlan(rows, existingCompanies);
  const newCompanies = plan.filter((c) => !c.existingId);
  const existingMatches = plan.filter((c) => c.existingId);
  const existingCompanyIds = existingMatches
    .map((company) => company.existingId)
    .filter((id): id is string => !!id)
    .sort()
    .join("|");

  useEffect(() => {
    if (!confirmOpen || !existingCompanyIds || !seatManagementEnabled) {
      setCompanySeats({});
      setLoadingSeats(false);
      return;
    }

    const companyIds = existingCompanyIds.split("|");
    setLoadingSeats(true);
    Promise.all(
      companyIds.map(
        async (companyId) =>
          [companyId, await apiClient.getCompanySeats(companyId)] as const,
      ),
    )
      .then((entries) => {
        setCompanySeats((previous) => ({
          ...previous,
          ...Object.fromEntries(entries),
        }));
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load seats");
      })
      .finally(() => setLoadingSeats(false));
  }, [confirmOpen, existingCompanyIds, seatManagementEnabled]);

  const getSettings = (companyName: string): CompanySettings => {
    const key = companyName.trim().toLowerCase();
    return companySettings[key] ?? DEFAULT_SETTINGS;
  };

  const updateCompanySettings = (
    companyName: string,
    updates: Partial<CompanySettings>,
  ) => {
    const key = companyName.trim().toLowerCase();
    setCompanySettings((prev) => ({
      ...prev,
      [key]: { ...getSettings(companyName), ...updates },
    }));
  };

  const getCompanyAdminUser = (company: CompanyPlan) => {
    const selectedRowId = getSettings(company.name).adminRowId;
    return (
      company.users.find((user) => user.rowId === selectedRowId) ??
      company.users[0]!
    );
  };

  const setCompanyAdminUser = (companyName: string, rowId: number) => {
    updateCompanySettings(companyName, { adminRowId: rowId });
  };

  const applyToAllNewCompanies = (updates: Partial<CompanySettings>) => {
    setCompanySettings((prev) => {
      const next = { ...prev };
      for (const company of newCompanies) {
        const key = company.name.trim().toLowerCase();
        next[key] = { ...(next[key] ?? DEFAULT_SETTINGS), ...updates };
      }
      return next;
    });
  };

  const rowSeatDefaults = rows.reduce<Record<number, UserSeatChoice>>(
    (acc, row) => {
      acc[row.id] = getRowSeatChoice(row);
      return acc;
    },
    {},
  );

  const getSeatChoice = (companyName: string, rowId: number): UserSeatChoice =>
    seatManagementEnabled
      ? (seatChoices[getSeatChoiceKey(companyName, rowId)] ??
        rowSeatDefaults[rowId] ??
        DEFAULT_SEAT_CHOICE)
      : { ...DEFAULT_SEAT_CHOICE, mode: "none" };

  const updateSeatChoice = (
    companyName: string,
    rowId: number,
    nextChoice: UserSeatChoice,
  ) => {
    setSeatChoices((previous) => ({
      ...previous,
      [getSeatChoiceKey(companyName, rowId)]: nextChoice,
    }));
  };

  const getAvailableSeats = (companyId?: string) =>
    companyId
      ? (companySeats[companyId] ?? []).filter((seat) => !seat.assignedUserId)
      : [];

  const getSelectedSeatIds = (company: CompanyPlan, excludingRowId?: number) =>
    new Set(
      company.users
        .filter((user) => user.rowId !== excludingRowId)
        .map((user) => getSeatChoice(company.name, user.rowId))
        .filter((choice) => choice.mode === "assign" && choice.seatId)
        .map((choice) => choice.seatId as string),
    );

  const findAssignableSeat = (
    company: CompanyPlan,
    seatType: SeatType,
    rowId: number,
  ) => {
    const selectedSeatIds = getSelectedSeatIds(company, rowId);
    return getAvailableSeats(company.existingId).find(
      (seat) => getSeatType(seat) === seatType && !selectedSeatIds.has(seat.id),
    );
  };

  const setSeatMode = (
    company: CompanyPlan,
    user: CompanyPlan["users"][number],
    mode: SeatMode,
  ) => {
    const current = getSeatChoice(company.name, user.rowId);
    const nextChoice: UserSeatChoice = { ...current, mode };

    if (mode === "assign") {
      nextChoice.seatId = findAssignableSeat(
        company,
        current.seatType,
        user.rowId,
      )?.id;
    } else {
      delete nextChoice.seatId;
    }

    updateSeatChoice(company.name, user.rowId, nextChoice);
  };

  const setSeatType = (
    company: CompanyPlan,
    user: CompanyPlan["users"][number],
    seatType: SeatType,
  ) => {
    const current = getSeatChoice(company.name, user.rowId);
    const nextChoice: UserSeatChoice = { ...current, seatType };

    if (current.mode === "assign") {
      nextChoice.seatId = findAssignableSeat(company, seatType, user.rowId)?.id;
    }

    updateSeatChoice(company.name, user.rowId, nextChoice);
  };

  const importErrors = plan.flatMap((company) => {
    const errors: string[] = [];
    const maxUserCount = !company.existingId
      ? parseMaxUserCount(getSettings(company.name).maxUserCount)
      : null;

    if (!company.existingId) {
      if (
        Number.isNaN(maxUserCount) ||
        (maxUserCount !== null && maxUserCount < 1)
      ) {
        errors.push(
          `${company.name}: Max Users must be blank or a positive number.`,
        );
      } else if (maxUserCount !== null && maxUserCount < company.users.length) {
        errors.push(
          `${company.name}: Max Users must be blank or at least ${company.users.length}.`,
        );
      }
    }

    const selectedSeatIds = new Set<string>();
    const availableSeats = getAvailableSeats(company.existingId);

    for (const user of company.users) {
      const choice = getSeatChoice(company.name, user.rowId);
      if (choice.mode !== "assign") continue;

      if (!company.existingId) {
        errors.push(
          `${user.email}: New companies do not have seats to assign yet.`,
        );
        continue;
      }

      if (!choice.seatId) {
        errors.push(
          `${user.email}: Select a ${formatSeatType(choice.seatType)} seat to assign.`,
        );
        continue;
      }

      const seat = availableSeats.find(
        (candidate) => candidate.id === choice.seatId,
      );
      if (!seat) {
        errors.push(`${user.email}: Selected seat is no longer available.`);
        continue;
      }

      if (getSeatType(seat) !== choice.seatType) {
        errors.push(
          `${user.email}: Selected seat does not match the selected seat type.`,
        );
      }

      if (selectedSeatIds.has(choice.seatId)) {
        errors.push(
          `${user.email}: Seat ${choice.seatId.slice(0, 8)} is selected more than once.`,
        );
      }
      selectedSeatIds.add(choice.seatId);
    }

    return errors;
  });

  const hasImportErrors = importErrors.length > 0;

  const getCreateUserBody = (
    companyName: string,
    user: CompanyPlan["users"][number],
  ): CreateUserBody => {
    const choice = getSeatChoice(companyName, user.rowId);
    const body: CreateUserBody = {
      email: user.email,
      firstName: user.firstName || undefined,
      lastName: user.lastName || undefined,
    };

    if (choice.mode === "create") {
      const isFlexible = choice.seatType === "flexible";
      body.seat = {
        isFlexible,
        autoReassign: isFlexible,
      };
    }

    return body;
  };

  const applySeatChoice = async ({
    companyId,
    companyName,
    user,
    userId,
  }: {
    companyId: string;
    companyName: string;
    user: CompanyPlan["users"][number];
    userId: string;
  }) => {
    if (!seatManagementEnabled) return undefined;

    const choice = getSeatChoice(companyName, user.rowId);

    if (choice.mode === "assign") {
      if (!choice.seatId) throw new Error(`No seat selected for ${user.email}`);
      await apiClient.assignCompanySeat(companyId, choice.seatId, userId);
      return `${formatSeatLabel({
        id: choice.seatId,
        isFlexible: choice.seatType === "flexible",
      } as SeatWithUser)} assigned`;
    }

    if (choice.mode === "create") {
      const isFlexible = choice.seatType === "flexible";
      const response = await apiClient.addCompanySeats(
        companyId,
        1,
        isFlexible,
      );
      const seat = response.seats.find(
        (candidate) =>
          !candidate.assignedUserId &&
          getSeatType(candidate) === choice.seatType,
      );
      if (!seat) throw new Error(`Could not create a seat for ${user.email}`);
      await apiClient.assignCompanySeat(companyId, seat.id, userId);
      return `${formatSeatType(choice.seatType)} seat created and assigned`;
    }

    return undefined;
  };

  // ---- Creation ----
  const handleConfirmCreate = async () => {
    if (hasErrors || hasImportErrors || loadingSeats) {
      setError("Fix validation errors before creating users.");
      return;
    }

    setCreating(true);
    setResults([]);
    setError(null);

    // New companies: 1 op for the company (incl. admin) + remaining users; existing: 1 op per user.
    const totalOps = plan.reduce((sum, c) => {
      if (c.existingId) return sum + c.users.length;
      return sum + 1 + (c.users.length - 1);
    }, 0);
    setProgressTotal(totalOps);
    setProgressIndex(0);

    const allResults: CreationResult[] = [];
    let idx = 0;

    for (const company of plan) {
      if (company.existingId) {
        for (const user of company.users) {
          try {
            const createdUser = await apiClient.createUser(
              company.existingId,
              getCreateUserBody(company.name, user),
            );
            const choice = getSeatChoice(company.name, user.rowId);
            const seatMessage =
              choice.mode === "assign"
                ? await applySeatChoice({
                    companyId: company.existingId,
                    companyName: company.name,
                    user,
                    userId: createdUser.userId,
                  })
                : choice.mode === "create"
                  ? `${formatSeatType(choice.seatType)} seat created and assigned`
                  : undefined;
            allResults.push({
              type: "user",
              name: `${user.email} → ${company.name}`,
              success: true,
              message: seatMessage
                ? `User added to existing company; ${seatMessage}`
                : "User added to existing company",
            });
          } catch (err) {
            allResults.push({
              type: "user",
              name: `${user.email} → ${company.name}`,
              success: false,
              message: err instanceof Error ? err.message : "Failed",
            });
          }
          idx++;
          setProgressIndex(idx);
          setResults([...allResults]);
        }
      } else {
        // New company - create company with the selected user as admin
        const adminUser = getCompanyAdminUser(company);
        const remainingUsers = company.users.filter(
          (user) => user.rowId !== adminUser.rowId,
        );
        const settings = getSettings(company.name);

        const companyData: CreateCompanyBody = {
          name: company.name,
          tier: settings.tier,
          maxUserCount: parseMaxUserCount(settings.maxUserCount),
          expiryDate: settings.expiryDate,
          adminEmail: adminUser.email,
          adminFirstName: adminUser.firstName || undefined,
          adminLastName: adminUser.lastName || undefined,
        };

        try {
          const result = await apiClient.createCompany(companyData);
          allResults.push({
            type: "company",
            name: `${company.name} (admin: ${adminUser.email})`,
            success: true,
            message: `Created company ${result.companyId}`,
          });
          idx++;
          setProgressIndex(idx);
          setResults([...allResults]);

          const adminSeatChoice = getSeatChoice(company.name, adminUser.rowId);
          if (adminSeatChoice.mode !== "none") {
            try {
              const seatMessage = await applySeatChoice({
                companyId: result.companyId,
                companyName: company.name,
                user: adminUser,
                userId: result.adminUserId,
              });
              allResults.push({
                type: "seat",
                name: `${adminUser.email} → ${company.name}`,
                success: true,
                message: seatMessage ?? "No seat change requested",
              });
              setResults([...allResults]);
            } catch (err) {
              allResults.push({
                type: "seat",
                name: `${adminUser.email} → ${company.name}`,
                success: false,
                message: err instanceof Error ? err.message : "Failed",
              });
              setResults([...allResults]);
            }
          }

          // Create remaining users after the admin exists
          for (const user of remainingUsers) {
            try {
              const createdUser = await apiClient.createUser(
                result.companyId,
                getCreateUserBody(company.name, user),
              );
              const choice = getSeatChoice(company.name, user.rowId);
              const seatMessage =
                choice.mode === "assign"
                  ? await applySeatChoice({
                      companyId: result.companyId,
                      companyName: company.name,
                      user,
                      userId: createdUser.userId,
                    })
                  : choice.mode === "create"
                    ? `${formatSeatType(choice.seatType)} seat created and assigned`
                    : undefined;
              allResults.push({
                type: "user",
                name: `${user.email} → ${company.name}`,
                success: true,
                message: seatMessage
                  ? `User created; ${seatMessage}`
                  : "User created",
              });
            } catch (err) {
              allResults.push({
                type: "user",
                name: `${user.email} → ${company.name}`,
                success: false,
                message: err instanceof Error ? err.message : "Failed",
              });
            }
            idx++;
            setProgressIndex(idx);
            setResults([...allResults]);
          }
        } catch (err) {
          allResults.push({
            type: "company",
            name: company.name,
            success: false,
            message:
              err instanceof Error ? err.message : "Failed to create company",
          });
          idx++;
          // Skip users for this company since company creation failed
          idx += remainingUsers.length;
          setProgressIndex(idx);
          setResults([...allResults]);
        }
      }
    }

    setCreating(false);
  };

  const isComplete = results.length > 0 && !creating;
  const successCount = results.filter((r) => r.success).length;
  const failCount = results.filter((r) => !r.success).length;
  const importTableColumnCount = seatManagementEnabled ? 7 : 5;

  // ---- Reset ----
  const handleReset = () => {
    setPasteValue("");
    setRows([]);
    setResults([]);
    setConfirmOpen(false);
    setProgressIndex(0);
    setProgressTotal(0);
    setError(null);
    loadCompanies();
    setCompanySettings({});
    setSeatChoices({});
    setCompanySeats({});
    setLoadingSeats(false);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography variant="h5">Bulk Import</Typography>
        {loadingCompanies && <CircularProgress size={18} />}
        {!loadingCompanies && (
          <Typography variant="body2" color="text.secondary">
            {existingCompanies.length} existing{" "}
            {existingCompanies.length === 1 ? "company" : "companies"} loaded
          </Typography>
        )}
      </Box>

      {rows.length === 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <ContentPasteIcon color="action" />
            <Typography variant="h6">Paste spreadsheet data</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste rows from Excel, Google Sheets, or CSV. Expected columns:{" "}
            <strong>Company Name, Email, First Name, Last Name</strong>.
            {seatManagementEnabled && (
              <>
                Optional columns: <strong>Seat</strong> (<code>create</code>,{" "}
                <code>assign</code>,<code>none</code>; default{" "}
                <code>create</code>) and <strong>Seat Type</strong> (
                <code>named</code>, <code>flexible</code>; default{" "}
                <code>named</code>).{" "}
              </>
            )}
            Headers are auto-detected. Tab or comma delimited. If a company name
            matches an existing company, users will be added to it.
          </Typography>
          <TextField
            multiline
            minRows={6}
            maxRows={20}
            fullWidth
            variant="outlined"
            placeholder={
              seatManagementEnabled
                ? `Company\tEmail\tFirst Name\tLast Name\tSeat\tSeat Type\nAcme Inc\tjohn@acme.com\tJohn\tDoe\tcreate\tnamed\nAcme Inc\tjane@acme.com\tJane\tSmith\tassign\tflexible\nGlobex Corp\tbob@globex.com\tBob\tJones\tnone\tnamed`
                : `Company\tEmail\tFirst Name\tLast Name\nAcme Inc\tjohn@acme.com\tJohn\tDoe\nAcme Inc\tjane@acme.com\tJane\tSmith\nGlobex Corp\tbob@globex.com\tBob\tJones`
            }
            value={pasteValue}
            onChange={(e) => setPasteValue(e.target.value)}
            sx={{ mb: 2, fontFamily: "monospace", fontSize: 13 }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            variant="contained"
            onClick={handleParse}
            disabled={!pasteValue.trim()}
          >
            Parse Data
          </Button>
        </Paper>
      )}

      {rows.length > 0 && !confirmOpen && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Imported Rows ({rows.length})</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={handleReset}>
                Start Over
              </Button>
              <Button size="small" startIcon={<AddIcon />} onClick={addRow}>
                Add Row
              </Button>
            </Box>
          </Box>

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 180 }}>Company Name</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>First Name</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Last Name</TableCell>
                  {seatManagementEnabled && (
                    <TableCell sx={{ minWidth: 130 }}>Seat</TableCell>
                  )}
                  {seatManagementEnabled && (
                    <TableCell sx={{ minWidth: 130 }}>Seat Type</TableCell>
                  )}
                  <TableCell sx={{ width: 60 }} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const rowErrors = validationErrors[row.id] ?? [];
                  const companyError = rowErrors.find((message) =>
                    message.includes("Company"),
                  );
                  const emailError = rowErrors.find(
                    (message) =>
                      message.includes("email") || message.includes("Email"),
                  );
                  const seatModeError = rowErrors.find((message) =>
                    message.includes("Seat action"),
                  );
                  const seatTypeError = rowErrors.find((message) =>
                    message.includes("Seat type"),
                  );
                  const seatMode = parseSeatMode(row.seatMode);
                  const seatType = parseSeatType(row.seatType);

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        sx={
                          rowErrors.length > 0
                            ? { bgcolor: "error.50" }
                            : undefined
                        }
                      >
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            variant="standard"
                            value={row.companyName}
                            onChange={(event) =>
                              updateRow(
                                row.id,
                                "companyName",
                                event.target.value,
                              )
                            }
                            error={!!companyError}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            variant="standard"
                            value={row.email}
                            onChange={(event) =>
                              updateRow(row.id, "email", event.target.value)
                            }
                            error={!!emailError}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            variant="standard"
                            value={row.firstName}
                            onChange={(event) =>
                              updateRow(row.id, "firstName", event.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            variant="standard"
                            value={row.lastName}
                            onChange={(event) =>
                              updateRow(row.id, "lastName", event.target.value)
                            }
                          />
                        </TableCell>
                        {seatManagementEnabled && (
                          <TableCell>
                            <FormControl
                              fullWidth
                              size="small"
                              variant="standard"
                              error={!!seatModeError}
                            >
                              <Select
                                value={seatMode ?? ""}
                                displayEmpty
                                inputProps={{ "aria-label": "Seat action" }}
                                onChange={(event) =>
                                  updateRow(
                                    row.id,
                                    "seatMode",
                                    event.target.value,
                                  )
                                }
                              >
                                {!seatMode && row.seatMode.trim() && (
                                  <MenuItem value="" disabled>
                                    Invalid: {row.seatMode}
                                  </MenuItem>
                                )}
                                <MenuItem value="create">Create</MenuItem>
                                <MenuItem value="assign">Assign</MenuItem>
                                <MenuItem value="none">None</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        )}
                        {seatManagementEnabled && (
                          <TableCell>
                            <FormControl
                              fullWidth
                              size="small"
                              variant="standard"
                              error={!!seatTypeError}
                            >
                              <Select
                                value={seatType ?? ""}
                                displayEmpty
                                inputProps={{ "aria-label": "Seat type" }}
                                onChange={(event) =>
                                  updateRow(
                                    row.id,
                                    "seatType",
                                    event.target.value,
                                  )
                                }
                              >
                                {!seatType && row.seatType.trim() && (
                                  <MenuItem value="" disabled>
                                    Invalid: {row.seatType}
                                  </MenuItem>
                                )}
                                <MenuItem value="named">Named</MenuItem>
                                <MenuItem value="flexible">Flexible</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Tooltip title="Remove row">
                            <IconButton
                              size="small"
                              onClick={() => deleteRow(row.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {rowErrors.length > 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={importTableColumnCount}
                            sx={{ py: 0 }}
                          >
                            <Alert
                              severity="warning"
                              variant="outlined"
                              sx={{ my: 1, py: 0.5 }}
                            >
                              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {rowErrors.map((message) => (
                                  <li key={message}>{message}</li>
                                ))}
                              </Box>
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {hasErrors && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Some rows have validation errors. Fix them before submitting.
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => setConfirmOpen(true)}
              disabled={rows.length === 0 || hasErrors}
            >
              Review & Create
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog
        open={confirmOpen}
        onClose={creating ? undefined : () => setConfirmOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isComplete ? "Creation Results" : "Confirm Bulk Creation"}
        </DialogTitle>
        <DialogContent>
          {!creating && !isComplete && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The following companies and users will be created. For new
                companies, choose which user becomes the admin. All users will
                receive an email with temporary credentials.
              </Typography>

              {loadingSeats && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Loading available seats for existing companies...
                </Alert>
              )}

              {hasImportErrors && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {importErrors.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </Box>
                </Alert>
              )}

              {/* Apply-all controls for new companies */}
              {newCompanies.length > 1 && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 2, bgcolor: "action.hover" }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Defaults for all new companies
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Tier</InputLabel>
                      <Select
                        value={getSettings(newCompanies[0].name).tier}
                        label="Tier"
                        onChange={(e) =>
                          applyToAllNewCompanies({
                            tier: e.target.value as CompanyTierEnum,
                          })
                        }
                      >
                        {Object.values(CompanyTierEnum).map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Expiry Date"
                      type="date"
                      value={getSettings(newCompanies[0].name).expiryDate}
                      onChange={(e) =>
                        applyToAllNewCompanies({ expiryDate: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 170 }}
                    />
                    <TextField
                      size="small"
                      label="Max Users"
                      type="number"
                      value={getSettings(newCompanies[0].name).maxUserCount}
                      onChange={(e) =>
                        applyToAllNewCompanies({ maxUserCount: e.target.value })
                      }
                      inputProps={{ min: 1 }}
                      helperText="Blank = unlimited"
                      sx={{ width: 140 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => applyToAllNewCompanies(DEFAULT_SETTINGS)}
                    >
                      Reset
                    </Button>
                  </Box>
                </Paper>
              )}

              {plan.map((company) => {
                const settings = getSettings(company.name);
                const availableSeats = getAvailableSeats(company.existingId);
                const adminUser = !company.existingId
                  ? getCompanyAdminUser(company)
                  : undefined;
                return (
                  <Paper
                    key={company.name}
                    variant="outlined"
                    sx={{ p: 2, mb: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {company.name}
                      </Typography>
                      {company.existingId ? (
                        <Chip
                          label="Existing Company"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="New Company"
                          size="small"
                          color="primary"
                        />
                      )}
                      <Chip
                        label={`${company.users.length} user${company.users.length > 1 ? "s" : ""}`}
                        size="small"
                        variant="outlined"
                      />
                      {seatManagementEnabled && company.existingId && (
                        <Chip
                          label={`${availableSeats.length} available seat${availableSeats.length === 1 ? "" : "s"}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Per-company settings for new companies */}
                    {!company.existingId && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          mb: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Tier</InputLabel>
                          <Select
                            value={settings.tier}
                            label="Tier"
                            onChange={(e) =>
                              updateCompanySettings(company.name, {
                                tier: e.target.value as CompanyTierEnum,
                              })
                            }
                          >
                            {Object.values(CompanyTierEnum).map((t) => (
                              <MenuItem key={t} value={t}>
                                {t}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label="Expiry"
                          type="date"
                          value={settings.expiryDate}
                          onChange={(e) =>
                            updateCompanySettings(company.name, {
                              expiryDate: e.target.value,
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 160 }}
                        />
                        <TextField
                          size="small"
                          label="Max Users"
                          type="number"
                          value={settings.maxUserCount}
                          onChange={(e) =>
                            updateCompanySettings(company.name, {
                              maxUserCount: e.target.value,
                            })
                          }
                          inputProps={{ min: 1 }}
                          helperText="Blank = unlimited"
                          sx={{ width: 100 }}
                        />
                      </Box>
                    )}

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {!company.existingId && (
                              <TableCell sx={{ width: 80 }}>Admin</TableCell>
                            )}
                            <TableCell>User</TableCell>
                            {seatManagementEnabled && (
                              <>
                                <TableCell sx={{ width: 220 }}>
                                  Seat action
                                </TableCell>
                                <TableCell sx={{ width: 150 }}>
                                  Seat type
                                </TableCell>
                                <TableCell sx={{ width: 220 }}>Seat</TableCell>
                              </>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {company.users.map((user) => {
                            const choice = getSeatChoice(
                              company.name,
                              user.rowId,
                            );
                            const selectedSeatIds = getSelectedSeatIds(
                              company,
                              user.rowId,
                            );
                            const assignableSeats = availableSeats.filter(
                              (seat) =>
                                getSeatType(seat) === choice.seatType &&
                                (seat.id === choice.seatId ||
                                  !selectedSeatIds.has(seat.id)),
                            );
                            const canAssignSeat =
                              !!company.existingId && availableSeats.length > 0;

                            return (
                              <TableRow key={user.rowId}>
                                {!company.existingId && (
                                  <TableCell>
                                    <Radio
                                      size="small"
                                      checked={adminUser?.rowId === user.rowId}
                                      onChange={() =>
                                        setCompanyAdminUser(
                                          company.name,
                                          user.rowId,
                                        )
                                      }
                                      inputProps={{
                                        "aria-label": `Make ${user.email} admin`,
                                      }}
                                    />
                                  </TableCell>
                                )}
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {user.firstName} {user.lastName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {user.email}
                                    </Typography>
                                    {adminUser?.rowId === user.rowId && (
                                      <Chip
                                        label="Admin"
                                        size="small"
                                        color="info"
                                        sx={{ alignSelf: "flex-start" }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                {seatManagementEnabled && (
                                  <>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={choice.mode === "create"}
                                              onChange={(_, checked) =>
                                                setSeatMode(
                                                  company,
                                                  user,
                                                  checked ? "create" : "none",
                                                )
                                              }
                                            />
                                          }
                                          label="Create seat"
                                        />
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={choice.mode === "assign"}
                                              disabled={
                                                !canAssignSeat || loadingSeats
                                              }
                                              onChange={(_, checked) =>
                                                setSeatMode(
                                                  company,
                                                  user,
                                                  checked ? "assign" : "none",
                                                )
                                              }
                                            />
                                          }
                                          label="Assign existing seat"
                                        />
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <FormControl
                                        size="small"
                                        fullWidth
                                        disabled={choice.mode === "none"}
                                      >
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                          value={choice.seatType}
                                          label="Type"
                                          onChange={(event) =>
                                            setSeatType(
                                              company,
                                              user,
                                              event.target.value as SeatType,
                                            )
                                          }
                                        >
                                          <MenuItem value="named">
                                            Named
                                          </MenuItem>
                                          <MenuItem value="flexible">
                                            Flexible
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell>
                                      {choice.mode === "assign" ? (
                                        <FormControl size="small" fullWidth>
                                          <InputLabel>Seat</InputLabel>
                                          <Select
                                            value={choice.seatId ?? ""}
                                            label="Seat"
                                            onChange={(event) =>
                                              updateSeatChoice(
                                                company.name,
                                                user.rowId,
                                                {
                                                  ...choice,
                                                  seatId: event.target.value,
                                                },
                                              )
                                            }
                                          >
                                            <MenuItem value="">
                                              Select seat
                                            </MenuItem>
                                            {assignableSeats.map((seat) => (
                                              <MenuItem
                                                key={seat.id}
                                                value={seat.id}
                                              >
                                                {formatSeatLabel(seat)}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      ) : choice.mode === "create" ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          New {formatSeatType(choice.seatType)}
                                          seat
                                        </Typography>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          No seat
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                );
              })}

              <Alert severity="info">
                {newCompanies.length > 0 && (
                  <>
                    <strong>{newCompanies.length}</strong> new{" "}
                    {newCompanies.length === 1 ? "company" : "companies"} will
                    be created.{" "}
                  </>
                )}
                {existingMatches.length > 0 && (
                  <>
                    <strong>
                      {existingMatches.reduce((s, c) => s + c.users.length, 0)}
                    </strong>{" "}
                    {existingMatches.reduce((s, c) => s + c.users.length, 0) ===
                    1
                      ? "user"
                      : "users"}{" "}
                    will be added to <strong>{existingMatches.length}</strong>{" "}
                    existing{" "}
                    {existingMatches.length === 1 ? "company" : "companies"}
                    .{" "}
                  </>
                )}
                <strong>{plan.reduce((s, c) => s + c.users.length, 0)}</strong>{" "}
                users total will receive an email with temporary credentials.
              </Alert>
            </>
          )}

          {/* Progress */}
          {creating && (
            <Box sx={{ py: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <CircularProgress size={24} />
                <Typography>
                  Creating... ({progressIndex}/{progressTotal})
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  progressTotal > 0 ? (progressIndex / progressTotal) * 100 : 0
                }
                sx={{ mb: 2 }}
              />
            </Box>
          )}

          {/* Results */}
          {results.length > 0 && (
            <Box sx={{ mt: creating ? 0 : 2 }}>
              {isComplete && (
                <Alert
                  severity={failCount === 0 ? "success" : "warning"}
                  sx={{ mb: 2 }}
                >
                  Done! {successCount} succeeded, {failCount} failed.
                </Alert>
              )}
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Chip
                            label={
                              r.type === "company"
                                ? "Company"
                                : r.type === "seat"
                                  ? "Seat"
                                  : "User"
                            }
                            size="small"
                            color={
                              r.type === "company"
                                ? "primary"
                                : r.type === "seat"
                                  ? "info"
                                  : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={r.success ? "OK" : "Failed"}
                            size="small"
                            color={r.success ? "success" : "error"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{r.message}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!creating && !isComplete && (
            <>
              <Button onClick={() => setConfirmOpen(false)}>
                Back to Edit
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmCreate}
                disabled={loadingSeats || hasImportErrors}
              >
                Create All
              </Button>
            </>
          )}
          {isComplete && (
            <Button variant="contained" onClick={handleReset}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
